/**
 * ACCOUNTING — the Supercanic shop's money, read live.
 *
 * Anthony, 2026-10-08: "Add an accounting section in shop desk supercanic
 * for me and Alaina with all this kinds of data." Then: "just for us two."
 *
 * Payroll is the pressure, so three numbers sit at the top before anything
 * else: what came in this week, what is owed to us, and what is coming.
 * Everything under them is the list each number was added up from, so a
 * figure can always be traced back to the jobs behind it.
 *
 * The Books page is Kubera's monthly build across every business. This
 * page is one shop's own records, live, with no build step in between.
 *
 * TWO PEOPLE, ONE SHOP. canSeeAccounting (src/domain/books.ts) needs the
 * person on the list AND the desk standing in the Supercanic shop. The
 * server checks the same thing against the profile row before it reads a
 * single payment, so anybody else gets "not found" from the endpoint too.
 */

import { db } from "../data/client.ts";
import { esc, mount } from "../ui/dom.ts";
import { canSeeAccounting } from "../domain/books.ts";

type Tally = { total: number; count: number; by: Array<{ channel: string; label: string; total: number; count: number }> };
type Pay = {
  id: string; amount: number; reference: string | null; received_at: string; day: string;
  recorded_by: string | null; job_id: string | null; job_number: string | null; job_title: string | null;
  customer: string | null; invoice_kind: string | null; channel: string; channel_label: string;
};
type Owed = {
  job_id: string | null; invoice_number: string | null; kind: string | null; job_number: string | null;
  title: string | null; customer: string | null; issued: string | null; days: number | null;
  total: number; paid: number; balance: number;
};
type JobLine = {
  job_id: string; job_number: string | null; title: string | null; status: string; customer: string | null;
  when: string; value: number; taken: number; to_collect: number;
};
type Dispatcher = {
  name: string; earned_week: number; earned_month: number; earned_all: number; paid_all: number; owed: number;
  entries: Array<{ day: string; amount: number; note: string | null; basis: string }>;
  jobs_booked: number; jobs_paid: number; collected_month: number;
};
type Data = {
  shop: string; windows: { today: string; weekFrom: string; monthFrom: string }; read_at: string;
  came_in: { today: Tally; week: Tally; month: Tally };
  cash: { week: number; month: number; rows: Pay[] };
  payments: Pay[];
  owed: { total: number; invoiced: Owed[]; done_unbilled: JobLine[] };
  coming: { total: number; jobs: JobLine[] };
  dispatch: Dispatcher[];
  crew_paid_month: number;
};

const TABS = [
  { key: "in", label: "Came In" },
  { key: "owed", label: "Owed to Us" },
  { key: "coming", label: "Coming In" },
  { key: "cash", label: "Cash" },
  { key: "dispatch", label: "Dispatch" },
] as const;
type Tab = (typeof TABS)[number]["key"];

const usd = (n: number, cents = false): string =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD",
    minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 });

const dayName = (ymd: string): string => {
  const [y = 2026, m = 1, d = 1] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/* Rendered copy never carries a colon, semicolon or dash. A note typed at
   the desk can, so the note is cleaned before it is shown. */
const tidy = (s: string | null | undefined, max = 80): string => {
  const t = (s ?? "").replace(/[;:]/g, ",").replace(/\s[-–—]+\s/g, ", ").replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

const STATUS: Record<string, string> = {
  scheduled: "Scheduled", in_progress: "In the bay", completed: "Finished", invoiced: "Invoiced",
};

async function token(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

export async function accountingPage(profileId: string | null, accountId: string | null): Promise<void> {
  if (!canSeeAccounting(profileId, accountId)) {
    mount(`<div class="page"><header class="page-head">
      <a class="back" href="#/more">&larr; Everything else</a><h1>Not Found</h1></header></div>`);
    return;
  }

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Accounting</h1>
      <p>What came in, what is owed to us, and what is coming. Read live from the desk. Only you and Alaina see this page.</p></header>
    <div id="ac-body"><div class="loading">Adding it up&hellip;</div></div>
  </div>`);

  const host = document.getElementById("ac-body")!;
  let data: Data | { error: string };
  try {
    const r = await fetch("/api/accounting", { headers: { Authorization: `Bearer ${await token()}` } });
    data = await r.json() as Data | { error: string };
    if (!r.ok && !("error" in data)) data = { error: "Could not read the money just now." };
  } catch {
    data = { error: "Could not reach the desk. Check the connection and open this page again." };
  }
  if ("error" in data) { host.innerHTML = `<div class="card"><div class="empty bad">${esc(data.error)}</div></div>`; return; }
  const d = data;

  let tab: Tab = "in";
  const draw = () => {
    host.innerHTML = `
      ${top(d)}
      <div class="bk-tabs" role="tablist">
        ${TABS.map((t) => `<button type="button" role="tab" class="bk-tab${t.key === tab ? " on" : ""}"
          aria-selected="${t.key === tab}" data-tab="${t.key}">${esc(t.label)}</button>`).join("")}
      </div>
      <p class="bk-asof">Read live when this page opened, ${esc(dayName(d.windows.today))}.
        This week runs from ${esc(dayName(d.windows.weekFrom))}. Open the page again for the newest numbers.</p>
      ${tab === "in" ? cameIn(d) : tab === "owed" ? owed(d) : tab === "coming" ? coming(d) : tab === "cash" ? cash(d) : dispatch(d)}`;
    host.querySelectorAll<HTMLButtonElement>("[data-tab]").forEach((b) =>
      b.addEventListener("click", () => { tab = b.dataset["tab"] as Tab; draw(); }));
  };
  draw();
}

function tile(n: string, label: string, hint = "", cls = ""): string {
  return `<div class="tile ac-tile ${cls}"><div class="tile-n">${esc(n)}</div>
    <div class="tile-l">${esc(label)}</div>${hint ? `<div class="ac-hint">${esc(hint)}</div>` : ""}</div>`;
}

function top(d: Data): string {
  return `
    <div class="tiles ac-big">
      ${tile(usd(d.came_in.week.total), "Came In This Week", plural(d.came_in.week.count, "payment"), "ac-in")}
      ${tile(usd(d.owed.total), "Owed to Us", plural(d.owed.invoiced.length + d.owed.done_unbilled.length, "job"), "ac-owed")}
      ${tile(usd(d.coming.total), "Coming In", `${plural(d.coming.jobs.length, "job")} booked, before tax`, "ac-coming")}
    </div>
    <div class="tiles">
      ${tile(usd(d.came_in.today.total), "Today", plural(d.came_in.today.count, "payment"))}
      ${tile(usd(d.came_in.month.total), "Month to Date", plural(d.came_in.month.count, "payment"))}
      ${tile(usd(d.cash.month), "Cash Taken This Month")}
      ${tile(usd(d.crew_paid_month), "Paid to the Crew This Month")}
    </div>`;
}

function jobHref(id: string | null): string { return id ? ` href="#/job/${esc(id)}"` : ""; }

function payRow(p: Pay): string {
  const who = [p.customer, p.job_number ? `Job ${p.job_number}` : null].filter(Boolean).join(" · ") || "Payment";
  const sub = [dayName(p.day), p.channel_label, p.invoice_kind === "deposit" ? "Deposit" : null,
    p.recorded_by ? `Taken by ${p.recorded_by}` : null].filter(Boolean).join(" · ");
  const note = tidy(p.reference);
  return `<a class="row ac-row"${jobHref(p.job_id)}>
    <div class="row-main"><div class="row-title">${esc(who)}</div>
      <div class="row-sub">${esc(sub)}</div>
      ${note ? `<div class="row-sub quiet">${esc(note)}</div>` : ""}</div>
    <div class="row-side"><b>${esc(usd(p.amount, true))}</b>
      <div><span class="ac-pill ac-${esc(p.channel)}">${esc(p.channel_label)}</span></div></div></a>`;
}

function byMethod(d: Data): string {
  const labels = new Map<string, string>();
  for (const t of [d.came_in.week, d.came_in.month]) for (const b of t.by) labels.set(b.channel, b.label);
  if (!labels.size) return `<div class="empty">Nothing recorded this month yet.</div>`;
  const get = (t: Tally, c: string) => t.by.find((b) => b.channel === c);
  return `<table class="ac-table">
    <thead><tr><th>How It Was Paid</th><th>This Week</th><th>Month to Date</th></tr></thead>
    <tbody>${[...labels].map(([c, label]) => {
      const w = get(d.came_in.week, c), m = get(d.came_in.month, c);
      return `<tr><td><span class="ac-pill ac-${esc(c)}">${esc(label)}</span></td>
        <td>${w ? `${esc(usd(w.total, true))} <span class="quiet">(${w.count})</span>` : "·"}</td>
        <td>${m ? `${esc(usd(m.total, true))} <span class="quiet">(${m.count})</span>` : "·"}</td></tr>`;
    }).join("")}
    <tr class="ac-sum"><td>Total</td><td>${esc(usd(d.came_in.week.total, true))}</td><td>${esc(usd(d.came_in.month.total, true))}</td></tr>
    </tbody></table>`;
}

function cameIn(d: Data): string {
  const list = d.payments;
  return `
    <div class="card"><div class="card-head">By How It Was Paid</div><div class="pad ac-scroll">${byMethod(d)}</div></div>
    <div class="card"><div class="card-head">Every Payment This Week and Month</div>
      ${list.length ? list.map(payRow).join("") : `<div class="empty">Nothing recorded this month yet.</div>`}</div>`;
}

function owed(d: Data): string {
  const inv = d.owed.invoiced.map((i) => `<a class="row ac-row"${jobHref(i.job_id)}>
      <div class="row-main"><div class="row-title">${esc([i.customer, i.job_number ? `Job ${i.job_number}` : null].filter(Boolean).join(" · ") || `Invoice ${i.invoice_number ?? ""}`)}</div>
        <div class="row-sub">${esc([`Invoice ${i.invoice_number ?? ""}`.trim(), i.kind === "deposit" ? "Deposit asked for" : null,
          i.issued ? `Sent ${dayName(i.issued)}` : null, i.days != null ? `${plural(i.days, "day")} ago` : null].filter(Boolean).join(" · "))}</div>
        ${i.paid > 0 ? `<div class="row-sub quiet">${esc(usd(i.paid, true))} paid of ${esc(usd(i.total, true))}</div>` : ""}</div>
      <div class="row-side"><b>${esc(usd(i.balance, true))}</b></div></a>`).join("");
  const done = d.owed.done_unbilled.map((j) => jobRow(j, d.windows.today, "Finished")).join("");
  return `
    <div class="card"><div class="pad"><p class="bk-lead"><b>${esc(usd(d.owed.total))}</b> is owed on work we already did.
      Oldest first, so the ones to chase are at the top.</p></div></div>
    <div class="card"><div class="card-head">Invoices Not Paid</div>
      ${inv || `<div class="empty">Every invoice sent is paid.</div>`}</div>
    <div class="card"><div class="card-head">Finished Jobs With No Invoice</div>
      ${done || `<div class="empty">Every finished job has an invoice.</div>`}</div>`;
}

function jobRow(j: JobLine, today: string, word?: string): string {
  const late = j.status === "scheduled" && j.when < today;
  const sub = [STATUS[j.status] ?? word ?? j.status, dayName(j.when), late ? "Date has passed" : null,
    j.taken > 0 ? `${usd(j.taken, true)} already taken` : null].filter(Boolean).join(" · ");
  return `<a class="row ac-row"${jobHref(j.job_id)}>
    <div class="row-main"><div class="row-title">${esc([j.customer, j.job_number ? `Job ${j.job_number}` : null].filter(Boolean).join(" · ") || "Job")}</div>
      <div class="row-sub">${esc(sub)}</div>
      ${j.title ? `<div class="row-sub quiet">${esc(tidy(j.title))}</div>` : ""}</div>
    <div class="row-side"><b>${esc(j.value > 0 ? usd(j.to_collect, true) : "No price yet")}</b></div></a>`;
}

function coming(d: Data): string {
  return `
    <div class="card"><div class="pad"><p class="bk-lead"><b>${esc(usd(d.coming.total))}</b> is booked and still to collect,
      across ${esc(plural(d.coming.jobs.length, "job"))}. Priced from the job lines before tax, less any deposit already taken.</p></div></div>
    <div class="card"><div class="card-head">Booked and in the Bay</div>
      ${d.coming.jobs.length ? d.coming.jobs.map((j) => jobRow(j, d.windows.today)).join("") : `<div class="empty">Nothing booked right now.</div>`}</div>`;
}

function cash(d: Data): string {
  return `
    <div class="tiles">
      ${tile(usd(d.cash.week, true), "Cash This Week")}
      ${tile(usd(d.cash.month, true), "Cash This Month")}
    </div>
    <div class="card"><div class="pad"><p class="bk-lead">Every cash payment this month and who took it.
      Use it to match the cash in hand against the bank.</p></div></div>
    <div class="card"><div class="card-head">Cash Payments</div>
      ${d.cash.rows.length ? d.cash.rows.map(payRow).join("") : `<div class="empty">No cash recorded this month yet.</div>`}</div>`;
}

function dispatch(d: Data): string {
  if (!d.dispatch.length) return `<div class="card"><div class="empty">Nobody is set up as a dispatcher on the Pay page.</div></div>`;
  return d.dispatch.map((p) => `
    <div class="card"><div class="card-head">${esc(p.name)}</div>
      <div class="pad">
        <div class="tiles">
          ${tile(usd(p.earned_week, true), "Earned This Week")}
          ${tile(usd(p.earned_month, true), "Earned This Month")}
          ${tile(usd(p.owed, true), "Still Owed to Him", `${usd(p.earned_all, true)} earned, ${usd(p.paid_all, true)} paid`)}
        </div>
        <p class="bk-lead">${esc(p.name.split(" ")[0] ?? p.name)} has booked <b>${esc(plural(p.jobs_booked, "job"))}</b> this month.
          ${esc(plural(p.jobs_paid, "is", "are"))} paid, and his jobs brought in <b>${esc(usd(p.collected_month, true))}</b> this month.</p>
      </div>
      ${p.entries.length ? p.entries.map((e) => `<div class="row">
          <div class="row-main"><div class="row-title">${esc(tidy(e.note) || "Commission")}</div>
            <div class="row-sub">${esc(dayName(e.day))}</div></div>
          <div class="row-side"><b>${esc(usd(e.amount, true))}</b></div></div>`).join("")
        : `<div class="empty">No commission entered for ${esc(p.name.split(" ")[0] ?? p.name)} yet. Add each call on his Pay page.</div>`}
      <div class="pad"><a class="btn ghost" href="#/pay">Open Pay</a></div>
    </div>`).join("");
}
